// @ts-nocheck - untyped
import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './WebScraper.scss';

const StatusChipWithInfo = ({
  status,
  jobId,
  errorMessage,
  canceledBy,
  cratedDate,
  completedAt,
  country,
  user,
  fileName,
  successRows,
  failedRows,
  processedRows,
  totalRows,
  partial,
  stopReason,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();

  const isPartial = Boolean(partial) && (status === 'Canceled' || status === 'Failed');
  const displayStatus = isPartial ? 'Partial' : status;

  const paletteKey =
    {
      Done: 'success',
      'In progress': 'info',
      Failed: 'error',
      Canceled: 'warning',
      Partial: 'warning',
    }[displayStatus] || 'info';
  const accent = theme.palette[paletteKey].main;
  const PILL_WIDTH = 120;
  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    width: PILL_WIDTH,
    height: 22,
    px: 1,
    borderRadius: '11px',
    bgcolor: alpha(accent, 0.12),
    color: theme.palette[paletteKey].dark || accent,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  };
  const dotSx = {
    width: 7,
    height: 7,
    borderRadius: '50%',
    bgcolor: accent,
    flexShrink: 0,
  };

  const shouldShowInfo = () => {
    return status === 'Failed' || status === 'Canceled' || status === 'Done';
  };

  const stopReasonLabel =
    stopReason === 'restart'
      ? 'Service restart'
      : stopReason === 'user'
        ? 'User cancellation'
        : null;

  const handleInfoClick = e => {
    e.stopPropagation();
    setDialogOpen(true);
  };

  const getStatusIcon = () => {
    if (status === 'Done') return <CheckCircleOutlineIcon fontSize="small" color="success" />;
    if (isPartial) return <CancelOutlinedIcon fontSize="small" color="warning" />;
    if (status === 'Failed') return <ErrorOutlineIcon fontSize="small" color="error" />;
    if (status === 'Canceled') return <CancelOutlinedIcon fontSize="small" color="warning" />;
    return null;
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const seconds = Math.round((new Date(end) - new Date(start)) / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (status === 'In progress') {
    const total = Number(totalRows) || 0;
    const done = Math.min(Number(processedRows) || 0, total);
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    const label = total > 0 ? `${done.toLocaleString()} / ${total.toLocaleString()}` : 'Starting…';

    return (
      <Tooltip title={total > 0 ? `In progress - ${percent}% complete` : 'In progress'} arrow>
        <div className="ws-status-cell">
          <Box sx={pillSx}>
            <Box sx={dotSx} className="ws-status-dot-live" />
            {label}
          </Box>
          <LinearProgress
            variant={total > 0 ? 'determinate' : 'indeterminate'}
            value={percent}
            color={paletteKey}
            className="ws-status-progress-bar"
            sx={{ bgcolor: alpha(accent, 0.14) }}
          />
        </div>
      </Tooltip>
    );
  }

  return (
    <>
      <div className="ws-status-inline">
        {isPartial ? (
          <Tooltip
            title={`${status} - partial report, rows scraped before the job stopped are available`}
            arrow
          >
            <Box sx={pillSx}>{displayStatus}</Box>
          </Tooltip>
        ) : (
          <Box sx={pillSx}>{displayStatus}</Box>
        )}
        {shouldShowInfo() && (
          <Tooltip title="View details" arrow>
            <IconButton size="small" onClick={handleInfoClick} className="ws-status-info-btn">
              <InfoOutlinedIcon fontSize="small" className="ws-status-info-icon" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="ws-detail-dialog-title">
          {getStatusIcon()}
          Job {displayStatus} Details
        </DialogTitle>

        <DialogContent dividers>
          <div className="ws-detail-dialog-body">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Job ID
              </Typography>
              <Typography variant="body2" className="ws-detail-mono">
                {jobId}
              </Typography>
            </Box>

            {status === 'Failed' && errorMessage && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Error Message
                </Typography>
                <Typography variant="body2" className="ws-detail-error-message">
                  {errorMessage}
                </Typography>
              </Box>
            )}

            {status === 'Done' && (
              <div className="ws-detail-grid-2col">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Success Rows
                  </Typography>
                  <Typography variant="body2" className="ws-detail-success">
                    {successRows ?? 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Failed Rows
                  </Typography>
                  <Typography
                    variant="body2"
                    className={failedRows > 0 ? 'ws-detail-failed-red' : 'ws-detail-failed'}
                  >
                    {failedRows ?? 0}
                  </Typography>
                </Box>
              </div>
            )}

            {status === 'Canceled' && canceledBy && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Canceled By
                </Typography>
                <Typography variant="body2" className="ws-detail-bold">
                  {canceledBy}
                </Typography>
              </Box>
            )}

            {status === 'Canceled' && stopReasonLabel && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Stop Reason
                </Typography>
                <Typography variant="body2" className="ws-detail-bold">
                  {stopReasonLabel}
                </Typography>
              </Box>
            )}

            {isPartial && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Partial Report
                </Typography>
                <Typography variant="body2">
                  The job ended as {status}. The report contains all rows scraped before it stopped
                  - download it from the Actions column.
                </Typography>
              </Box>
            )}

            {status === 'Canceled' && errorMessage && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reason
                </Typography>
                <Typography variant="body2">{errorMessage}</Typography>
              </Box>
            )}

            <div className="ws-detail-grid-2col">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Started
                </Typography>
                <Typography variant="body2">{new Date(cratedDate).toLocaleString()}</Typography>
              </Box>
              {completedAt && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body2">{new Date(completedAt).toLocaleString()}</Typography>
                </Box>
              )}
            </div>

            {cratedDate && completedAt && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body2">{formatDuration(cratedDate, completedAt)}</Typography>
              </Box>
            )}

            <div className="ws-detail-grid-2col">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Country
                </Typography>
                <Typography variant="body2">{country}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  User
                </Typography>
                <Typography variant="body2">{user}</Typography>
              </Box>
            </div>

            <Box>
              <Typography variant="caption" color="text.secondary">
                File Name
              </Typography>
              <Typography variant="body2" className="ws-detail-break">
                {fileName}
              </Typography>
            </Box>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StatusChipWithInfo;
